import m from '@bitstillery/mithril'
import {MithrilComponent} from '@bitstillery/mithril'
import {classes, unique_id} from '@bitstillery/common/lib/utils'
import {Button} from '@bitstillery/common/components/ui/button/button'
import {Dropdown} from '@bitstillery/common/components/ui/dropdown/dropdown'
import {FieldSelect} from '@bitstillery/common/components/fields/select/select'
import {FieldText} from '@bitstillery/common/components/fields/text/text'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {state} from '@bitstillery/mithril'

import type {FieldSelectManyAttrs} from '@bitstillery/common/types/field'

export interface SelectValue {
    value: number | string
    label: string
}

export class FieldSelectMany extends MithrilComponent<FieldSelectManyAttrs> {
    id = unique_id()

    data = state(
        {
            inline: {
                search: '',
                toggle: false,
            },
            selected: '',
        },
        `field-select-many-data-${this.id}`,
    )

    override oninit(vnode: m.Vnode<FieldSelectManyAttrs>) {
        const options = vnode.attrs.options ?? []
        // Augment the options with a disabled property if it is missing.
        for (const option of options) {
            if (!('disabled' in option)) {
                option.disabled = vnode.attrs.model.some((value) => value === option.value)
            }
        }
    }

    view(vnode: m.Vnode<FieldSelectManyAttrs>) {
        const options = vnode.attrs.options ?? []
        const selected_option = options.find((i) => i.value === this.data.selected)
        const variant = vnode.attrs.variant || 'default'

        // Sort the model array
        const sorted_model = [...vnode.attrs.model].toSorted((a, b) => {
            const option_a = options.find((opt) => opt.value === a)
            const option_b = options.find((opt) => opt.value === b)
            return String(option_a?.label ?? a).localeCompare(String(option_b?.label ?? b))
        })

        return (
            <div
                id={`c-field-multiselect-${this.id}`}
                className={classes(
                    vnode.attrs.composed ? null : 'c-field-select-many field',
                    `variant-${variant}`,
                    vnode.attrs.className,
                    {
                        disabled: vnode.attrs.disabled,
                    },
                )}
            >
                {/* Shows a regular select with options; the selected options are shown in a list below */}
                {variant === 'default' && [
                    <div className='control'>
                        <FieldSelect
                            composed={true}
                            label={vnode.attrs.label}
                            model={this.data.$selected}
                            options={options}
                            placeholder={vnode.attrs.placeholder ? vnode.attrs.placeholder : 'Select...'}
                        />
                        <Button
                            icon='plus'
                            disabled={!this.data.selected}
                            onclick={(event) => {
                                // If not yet in selection and something is selected
                                if (!vnode.attrs.model.includes(this.data.selected) && selected_option) {
                                    vnode.attrs.model.push(this.data.selected)
                                }
                                if (selected_option) {
                                    selected_option.disabled = true
                                }
                                this.data.selected = ''
                                if (vnode.attrs.onchange) {
                                    vnode.attrs.onchange(vnode.attrs.model)
                                }
                                event.preventDefault()
                            }}
                            tip='Add item to selection'
                            type='info'
                        />
                    </div>,
                    sorted_model.length > 0 ? (
                        <div className='selection-list'>
                            {sorted_model.map((value) => (
                                <div className='selection-item'>
                                    {(() => {
                                        const option = options.find((i) => i.value === value)
                                        return option?.label || value
                                    })()}
                                    <Icon
                                        name='trash'
                                        size='s'
                                        onclick={(e) => {
                                            e.preventDefault()
                                            const index = vnode.attrs.model.findIndex((i) => i === value)
                                            const clicked_option = options.find((i) => i.value === value)
                                            if (clicked_option) {
                                                clicked_option.disabled = false
                                            }

                                            m.redraw()
                                            if (index !== -1) {
                                                vnode.attrs.model.splice(index, 1)
                                            }
                                            if (vnode.attrs.onchange) {
                                                vnode.attrs.onchange(vnode.attrs.model)
                                            }
                                        }}
                                        type='unset'
                                    />
                                </div>
                            ))}
                        </div>
                    ) : null,
                ]}
                {/* The inline variant shows a dropdown with options; the selected options are toggled and shown in the same dropdown*/}
                {variant === 'inline' && [
                    vnode.attrs.label ? <label>{vnode.attrs.label}</label> : null,
                    <Dropdown
                        closeOnSelect={false}
                        disabled={vnode.attrs.disabled || options.length === 0}
                        model={this.data.inline}
                    >
                        <Button
                            className={classes('btn-dropdown', {
                                disabled: vnode.attrs.disabled || options.length === 0,
                            })}
                            icon={this.data.inline.toggle ? 'chevronDown' : 'chevronUp'}
                            text={(() => {
                                if (vnode.attrs.model.length === 0) {
                                    if (options.length > 0) {
                                        return 'Select...'
                                    }
                                    return 'No options...'
                                }
                                if (options.length === vnode.attrs.model.length) {
                                    return `All selected (${vnode.attrs.model.length})`
                                }
                                return `${vnode.attrs.model.length}/${options.length} selected...`
                            })()}
                            type='unset'
                            variant='menu'
                        />

                        {options.length > 0 && (
                            <div
                                className='dropdown-body'
                                onclick={(e) => {
                                    const checkbox =
                                        e.target instanceof Element
                                            ? e.target.closest<HTMLInputElement>('input[type="checkbox"]')
                                            : null
                                    if (checkbox) {
                                        const index = vnode.attrs.model.findIndex((i) => i === checkbox.value)
                                        if (index !== -1) {
                                            vnode.attrs.model.splice(index, 1)
                                        } else {
                                            vnode.attrs.model.push(checkbox.value)
                                        }

                                        if (vnode.attrs.onchange) {
                                            vnode.attrs.onchange(vnode.attrs.model)
                                        }
                                    }
                                }}
                            >
                                {options.length > 10 ? (
                                    <FieldText model={this.data.inline.$search} placeholder='Search...' />
                                ) : null}
                                <div className='scroll-container'>
                                    {options
                                        .filter((option) => {
                                            if (!this.data.inline.search) {
                                                return true
                                            }
                                            return option.label.toLowerCase().includes(this.data.inline.search.toLowerCase())
                                        })
                                        .map((option) => {
                                            const checked = vnode.attrs.model.some((value) => value === option.value)
                                            return (
                                                <label>
                                                    <input type='checkbox' checked={checked} value={option.value} />
                                                    <span>{option.label}</span>
                                                </label>
                                            )
                                        })}
                                </div>
                            </div>
                        )}
                    </Dropdown>,
                ]}
            </div>
        )
    }
}
